import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Fade,
  Skeleton,
  Pagination,
  LinearProgress,
  Paper,
  Tooltip,
  Avatar,
  Stack,
  Container,
  CardMedia,
  CardActions,
  Zoom,
  Slide
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  SelectAll as SelectAllIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Block as BlockIcon
} from '@mui/icons-material';

import { RadioButtonUnchecked as RadioButtonUncheckedIcon } from '@mui/icons-material';



const BannerImages = () => {
  const { t } = useTranslation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]);





  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Helper function to check if URL is a video
  const isVideo = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv', '.wmv', '.m4v'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext));
  };



  // Fetch images from API
  const fetchImages = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: offset.toString(),
        brand: getCurrentBrand()
      });






      const response = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await response.json();
      setImages(data.images || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);









  // Initial load
  useEffect(() => {
    fetchImages(currentPage);
  }, [currentPage, fetchImages]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };


  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const selectAllOnCurrentPage = () => {
    const ids = images.map(img => img.id).filter(Boolean);
    setSelectedIds(new Set(ids));
  };

  const selectAllAcrossPages = async () => {
    setBulkLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '10000', // Large limit to fetch all
        offset: '0',
        brand: getCurrentBrand()
      });





      const response = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch all images');

      const data = await response.json();
      const allImages = data.images || [];
      const ids = allImages.map(img => img.id).filter(Boolean);
      setSelectedIds(new Set(ids));
    } catch (err) {
      console.error('Error selecting all:', err);
      setError(err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkActivate = async () => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    const brand = getCurrentBrand();
    let success = 0, fail = 0;
    for (const id of selectedIds) {
      const img = images.find(i => i.id === id);
      if (!img) { fail++; continue; }
      try {
        const res = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}/${id}?brand=${brand}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_url: img.image_url,
            category: img.category,
            is_active: true
          })
        });
        if (res.ok) {
          success++;
        } else {
          fail++;
        }
      } catch (e) {
        console.error('Bulk activate error:', e);
        fail++;
      }
    }
    await fetchImages(currentPage);
    setBulkLoading(false);
    clearSelection();
    if (fail > 0) setError(`${success} activated, ${fail} failed.`); else setError(null);
  };

  const handleBulkDeactivate = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t('bannerImages.confirmBulkDeactivate') || 'Deactivate selected items?')) return;
    setBulkLoading(true);
    const brand = getCurrentBrand();
    let success = 0, fail = 0;
    for (const id of selectedIds) {
      const img = images.find(i => i.id === id);
      if (!img) { fail++; continue; }
      try {
        const res = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}/${id}?brand=${brand}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_url: img.image_url,
            category: img.category,
            is_active: false
          })
        });
        if (res.ok) {
          success++;
        } else {
          fail++;
        }
      } catch (e) {
        console.error('Bulk deactivate error:', e);
        fail++;
      }
    }
    await fetchImages(currentPage);
    setBulkLoading(false);
    clearSelection();
    if (fail > 0) setError(`${success} deactivated, ${fail} failed.`); else setError(null);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(t('bannerImages.confirmBulkDelete') || 'Delete selected items? This cannot be undone.')) return;
    setBulkLoading(true);
    const brand = getCurrentBrand();
    let success = 0, fail = 0;
    for (const id of selectedIds) {
      try {
        const res = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}/${id}?brand=${brand}`, { method: 'DELETE' });
        if (res.ok) {
          success++;
        } else {
          fail++;
        }
      } catch (e) {
        console.error('Bulk delete error:', e);
        fail++;
      }
    }
    await fetchImages(currentPage);
    setBulkLoading(false);
    clearSelection();
    if (fail > 0) setError(`${success} deleted, ${fail} failed.`); else setError(null);
  };

  // Handle image click
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
  };

  // Handle menu open
  const handleMenuOpen = (event, image) => {
    setAnchorEl(event.currentTarget);
    setSelectedImage(image);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedImage(null);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach((file, index) => {
      // Validate file type (images only)
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        errors.push(`${file.name}: ${t('bannerImages.notImageFile')}`);
        return;
      }
      // Validate file size (max 10MB for images)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        errors.push(`${file.name}: ${t('bannerImages.fileSizeExceeded')}`);
        return;
      }
      validFiles.push(file);
    });

    // Check if total files exceed limit (3)
    const remainingSlots = Math.max(0, 3 - totalCount);
    if (validFiles.length > remainingSlots) {
      errors.push(t('bannerImages.limitExceededSelection', { count: remainingSlots }));
      validFiles.splice(remainingSlots);
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    setSelectedFiles(validFiles);
  };

  // Handle upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError(t('bannerImages.selectFilesUpload'));
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);
      setUploadingFiles([]);

      const totalFiles = selectedFiles.length;
      let completedFiles = 0;
      const results = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Update uploading files list
        setUploadingFiles(prev => [...prev, { name: file.name, status: 'uploading' }]);

        try {
          // Create FormData
          const formData = new FormData();
          formData.append('image', file);

          // Upload to Cloudflare R2 with IG folder
          const uploadResponse = await fetch(`${API_ENDPOINTS.UPLOAD_TO_R2}?folder=lokss2-ig`, {
            method: 'POST',
            body: formData,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload ${file.name} to R2`);
          }

          const uploadResult = await uploadResponse.json();

          // Insert into lokss2_ig table
          const insertResponse = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}?brand=${getCurrentBrand()}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              image_url: uploadResult.url,
              is_active: true,
              category: null
            }),
          });

          if (!insertResponse.ok) {
            const errorData = await insertResponse.json();
            if (insertResponse.status === 403 && errorData.error === 'Quota exceeded') {
              throw new Error(errorData.message || 'Quota exceeded');
            }
            throw new Error(`Failed to save ${file.name} to database`);
          }



          // Update status to completed
          setUploadingFiles(prev =>
            prev.map(f => f.name === file.name ? { ...f, status: 'completed' } : f)
          );

          results.push({ file: file.name, success: true, url: uploadResult.url });
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);

        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          setUploadingFiles(prev =>
            prev.map(f => f.name === file.name ? { ...f, status: 'error', error: fileError.message } : f)
          );
          results.push({ file: file.name, success: false, error: fileError.message });
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);
        }
      }

      // Show results
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      if (failed > 0) {
        const failedFiles = results.filter(r => !r.success).map(r => `${r.file}: ${r.error}`).join(', ');
        setError(`${successful} ${t('bannerImages.filesUploaded')}, ${failed} ${t('bannerImages.failedFiles')}: ${failedFiles}`);
      } else {
        setError(null);
      }

      // Close dialog and refresh images after a short delay
      setTimeout(() => {
        setUploadDialogOpen(false);
        setSelectedFiles([]);
        setUploadProgress(0);
        setUploadingFiles([]);

        fetchImages(currentPage);

      }, 2000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Close upload dialog
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploadingFiles([]);

    setError(null);
  };

  // Handle image deletion
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm(t('bannerImages.deleteConfirm'))) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.BANNER_IMAGES}/${imageId}?brand=${brand}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(t('bannerImages.failedToDeleteImage'));
      }

      // Refresh the images list
      await fetchImages(currentPage);

    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <>
      <style>
        {`
          .card-overlay {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .MuiCard-root:hover .card-overlay {
            opacity: 1;
          }
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-10px) rotate(1deg); }
            50% { transform: translateY(-20px) rotate(0deg); }
            75% { transform: translateY(-10px) rotate(-1deg); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes shimmer-gradient {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          .shimmer {
            position: relative;
            overflow: hidden;
          }
          .shimmer::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: shimmer 2s infinite;
          }
          .pulse-animation {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
      <Container
        maxWidth="xl"
        sx={{
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 }
        }}
      >
        {/* Main Content Card Container */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          {/* Enhanced Professional Header Section */}
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
              color: 'white',
              borderRadius: 0,
              p: { xs: 3, md: 5 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: 'none'
            }}
          >
            {/* Animated Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.08,
                backgroundImage: `
              radial-gradient(circle at 20% 20%, white 1px, transparent 1px),
              radial-gradient(circle at 80% 80%, white 1px, transparent 1px),
              radial-gradient(circle at 40% 40%, white 1px, transparent 1px)
            `,
                backgroundSize: '60px 60px, 80px 80px, 40px 40px',
                animation: 'float 20s ease-in-out infinite'
              }}
            />

            {/* Floating Orbs */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '15%',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                animation: 'float 15s ease-in-out infinite reverse'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                left: '10%',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)',
                animation: 'float 12s ease-in-out infinite'
              }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ flexGrow: 1, mb: { xs: 3, md: 0 } }}>
                <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 2 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    width: { xs: 56, md: 72 },
                    height: { xs: 56, md: 72 },
                    border: '3px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(5deg)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <ImageIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        mb: 0.5,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                        textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        background: 'linear-gradient(45deg, #ffffff 0%, #f0f8ff 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {t('bannerImages.title')}
                    </Typography>
                    <Typography variant="h5" sx={{
                      opacity: 0.95,
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.3rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      letterSpacing: '0.01em'
                    }}>
                      {t('bannerImages.subtitle')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  className="pulse-animation"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    px: { xs: 3, md: 5 },
                    py: { xs: 1.5, md: 2.5 },
                    borderRadius: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    minHeight: { xs: 48, md: 64 },
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.35)',
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                      borderColor: 'rgba(255,255,255,0.6)',
                      animation: 'none'
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('bannerImages.uploadImages')}</Box>
                  <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>{t('common.upload') || 'Upload'}</Box>
                </Button>
              </Stack>
            </Stack>

          </Paper>

          {/* Content Section */}
          <Box sx={{
            p: { xs: 3, md: 4 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.3) 50%, transparent 100%)'
            }
          }}>


            {/* Error Alert */}
            {error && (
              <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.25rem'
                    }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError(null)}
                    >
                      <ErrorIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {error}
                </Alert>
              </Slide>
            )}

                {/* Filter Section - Same Style as Google/Facebook/XHS Pages */}


                {/* Selection Bar - Appears when items are selected */}
                {selectedIds.size > 0 && (
                  <Fade in timeout={300}>
                    <Paper
                      elevation={3}
                      sx={{
                        width: '100%',
                        mt: 2,
                        mb: 4, // Add spacing below
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile
                        alignItems: { xs: 'stretch', sm: 'center' }, // Stretch on mobile
                        gap: { xs: 2, sm: 2.5 },
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        border: '1px solid #e3f2fd',
                        borderRadius: 3,
                        px: { xs: 2, sm: 3 }, // Reduce padding on mobile
                        py: 2,
                        boxShadow: '0 4px 16px rgba(25, 118, 210, 0.12), 0 2px 4px rgba(0,0,0,0.08)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 3,
                          background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                          backgroundSize: '200% 100%',
                          animation: 'shimmer-gradient 3s ease-in-out infinite'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-start' }, gap: 2, flex: { xs: 'none', sm: 1 }, width: { xs: '100%', sm: 'auto' } }}>
                        <Chip
                          label={`${selectedIds.size} ${selectedIds.size === 1 ? 'item' : 'items'}`}
                          sx={{
                            bgcolor: '#1976d2',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            height: 28,
                            px: 1,
                            '& .MuiChip-label': {
                              px: 1.5
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#616161', fontSize: '0.875rem' }}>
                          selected
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                          <Tooltip title="Select all (across all pages)" arrow>
                            <IconButton
                              size="small"
                              onClick={selectAllAcrossPages}
                              disabled={images.length === 0 || bulkLoading}
                              sx={{
                                color: '#d32f2f', // Darker red
                                bgcolor: 'rgba(211, 47, 47, 0.1)',
                                border: '1px solid rgba(211, 47, 47, 0.3)',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  bgcolor: 'rgba(211, 47, 47, 0.2)',
                                  borderColor: '#d32f2f',
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 4px 12px rgba(211, 47, 47, 0.2)'
                                }
                              }}
                            >
                              {bulkLoading ? <CircularProgress size={20} color="inherit" /> : <SelectAllIcon sx={{ fontSize: 20 }} />}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Select all on current page" arrow>
                            <IconButton
                              size="small"
                              onClick={selectAllOnCurrentPage}
                              disabled={images.length === 0 || bulkLoading}
                              sx={{
                                color: '#1976d2', // Darker blue
                                bgcolor: 'rgba(25, 118, 210, 0.1)',
                                border: '1px solid rgba(25, 118, 210, 0.3)',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  bgcolor: 'rgba(25, 118, 210, 0.2)',
                                  borderColor: '#1976d2',
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)'
                                }
                              }}
                            >
                              <PlaylistAddCheckIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('bannerImages.clearSelection') || 'Clear selection'} arrow>
                            <IconButton
                              size="small"
                              onClick={clearSelection}
                              sx={{
                                color: '#757575',
                                bgcolor: 'rgba(117, 117, 117, 0.08)',
                                border: '1px solid rgba(117, 117, 117, 0.2)',
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  bgcolor: 'rgba(117, 117, 117, 0.16)',
                                  borderColor: '#757575',
                                  transform: 'scale(1.1) rotate(-5deg)',
                                  boxShadow: '0 4px 12px rgba(117, 117, 117, 0.3)'
                                }
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}>
                        {(() => {
                          // Check status of selected items
                          const selectedItems = images.filter(item => selectedIds.has(item.id));
                          const hasActiveItems = selectedItems.some(item => item.is_active !== false);
                          const hasInactiveItems = selectedItems.some(item => item.is_active === false);

                          return (
                            <>
                              {hasActiveItems && (
                                <Button
                                  size="medium"
                                  variant="contained"
                                  onClick={handleBulkDeactivate}
                                  disabled={bulkLoading}
                                  startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />}
                                  sx={{
                                    flex: { xs: 1, sm: 'none' }, // Expand on mobile
                                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: { xs: 1.5, sm: 2.5 },
                                    py: 1,
                                    borderRadius: 2,
                                    fontSize: '0.875rem',
                                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 16px rgba(255, 152, 0, 0.4)'
                                    },
                                    '&:disabled': {
                                      background: '#e0e0e0',
                                      color: '#9e9e9e',
                                      boxShadow: 'none'
                                    }
                                  }}
                                >
                                  {t('bannerImages.bulkDeactivate') || 'Deactivate Selected'}
                                </Button>
                              )}

                              {hasInactiveItems && (
                                <Button
                                  size="medium"
                                  variant="contained"
                                  onClick={handleBulkActivate}
                                  disabled={bulkLoading}
                                  startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
                                  sx={{
                                    flex: { xs: 1, sm: 'none' }, // Expand on mobile
                                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    px: { xs: 1.5, sm: 2.5 },
                                    py: 1,
                                    borderRadius: 2,
                                    fontSize: '0.875rem',
                                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)'
                                    },
                                    '&:disabled': {
                                      background: '#e0e0e0',
                                      color: '#9e9e9e',
                                      boxShadow: 'none'
                                    }
                                  }}
                                >
                                  {t('bannerImages.bulkActivate') || 'Activate Selected'}
                                </Button>
                              )}

                              <Button
                                size="medium"
                                variant="contained"
                                onClick={handleBulkDelete}
                                disabled={bulkLoading}
                                startIcon={bulkLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                                sx={{
                                  flex: { xs: 1, sm: 'none' }, // Expand on mobile
                                  background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                                  color: 'white',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  px: { xs: 1.5, sm: 2.5 },
                                  py: 1,
                                  borderRadius: 2,
                                  fontSize: '0.875rem',
                                  boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 16px rgba(244, 67, 54, 0.4)'
                                  },
                                  '&:disabled': {
                                    background: '#e0e0e0',
                                    color: '#9e9e9e',
                                    boxShadow: 'none'
                                  }
                                }}
                              >
                                {t('bannerImages.bulkDelete') || 'Delete Selected'}
                              </Button>
                            </>
                          );
                        })()}
                      </Box>
                    </Paper>
                  </Fade>
                )}


            {/* Enhanced Professional Loading State */}
            {
              loading && (
                <Fade in timeout={300}>
                  <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                    {[...Array(8)].map((_, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
                        <Card
                          sx={{
                            borderRadius: 4,
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            border: '2px solid transparent',
                            background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2c3e50 0%, #3498db 100%) border-box',
                            animation: 'float 6s ease-in-out infinite',
                            animationDelay: `${index * 0.2}s`,
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: '0 12px 30px rgba(52, 152, 219, 0.15)'
                            }
                          }}
                        >
                          <Box className="shimmer" sx={{ width: '100%', height: { xs: 240, sm: 280, md: 300 }, overflow: 'hidden' }}>
                            <Skeleton
                              variant="rectangular"
                              sx={{
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)',
                                backgroundSize: '200% 100%',
                                animation: 'loading 1.8s infinite',
                                borderBottom: '1px solid #e9ecef'
                              }}
                            />
                          </Box>
                          <CardContent sx={{ p: 3 }}>
                            <Stack spacing={2}>
                              <Skeleton variant="text" height={24} width="60%" sx={{ mb: 1 }} />
                              <Skeleton variant="text" height={16} width="80%" sx={{ mb: 2 }} />
                              <Stack direction="row" spacing={1}>
                                <Skeleton variant="rectangular" height={24} width={60} sx={{ borderRadius: 2 }} />
                                <Skeleton variant="rectangular" height={24} width={70} sx={{ borderRadius: 2 }} />
                              </Stack>
                            </Stack>
                          </CardContent>
                          <CardActions sx={{ p: 3, pt: 2, pb: 2, minHeight: 72, justifyContent: 'center' }}>
                            <Skeleton variant="rectangular" height={36} width={120} sx={{ borderRadius: 2 }} />
                          </CardActions>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Fade>
              )
            }

            {/* Enhanced Images Grid */}
            {
              !loading && (
                <Fade in timeout={500}>
                  <Box sx={{
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -20,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '60%',
                      height: '2px',
                      background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.2) 50%, transparent 100%)',
                      borderRadius: '2px'
                    }
                  }}>
                    {images.length === 0 ? (
                      <Paper
                        elevation={2}
                        sx={{
                          p: 6,
                          textAlign: 'center',
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                          border: '1px solid #dee2e6'
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 3,
                            bgcolor: 'rgba(52, 152, 219, 0.1)'
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />
                        </Avatar>
                        <Typography variant="h5" sx={{ color: '#495057', mb: 2, fontWeight: 600 }}>
                          {t('bannerImages.noImages')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#6c757d', mb: 3 }}>
                          {t('bannerImages.noImages')}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<UploadIcon />}
                          onClick={() => setUploadDialogOpen(true)}
                          sx={{
                            background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)'
                            },
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          {t('bannerImages.uploadImages')}
                        </Button>
                      </Paper>
                    ) : (
                      <>
                        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                          {images.map((image, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={image.id}>
                              <Zoom in timeout={300 + (index * 100)}>
                                <Card
                                  sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    border: selectedIds.has(image.id) ? '2px solid #1d4ed8' : '2px solid transparent',
                                    background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2c3e50 0%, #3498db 100%) border-box',
                                    position: 'relative',
                                    '@media (hover: hover)': {
                                      '&:hover': {
                                        transform: 'translateY(-12px) scale(1.03)',
                                        boxShadow: '0 25px 50px rgba(52, 152, 219, 0.25)',
                                        '&::before': {
                                          opacity: 1
                                        }
                                      }
                                    },
                                    '&::before': {
                                      content: '""',
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.1) 0%, rgba(52, 152, 219, 0.1) 100%)',
                                      opacity: 0,
                                      transition: 'opacity 0.4s ease',
                                      zIndex: 1,
                                      pointerEvents: 'none'
                                    }
                                  }}
                                  onClick={() => handleImageClick(image)}
                                >
                                  <Box sx={{ position: 'relative', zIndex: 2, width: '100%', height: { xs: 240, sm: 280, md: 300 }, overflow: 'hidden' }}>

                                    {isVideo(image.image_url) ? (
                                      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                        <CardMedia
                                          component="video"
                                          src={image.image_url}
                                          sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                              transform: 'scale(1.05)'
                                            }
                                          }}
                                          preload="metadata"
                                          muted
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                        <Box
                                          sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            bgcolor: 'rgba(0, 0, 0, 0.6)',
                                            borderRadius: '50%',
                                            width: 56,
                                            height: 56,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none',
                                            zIndex: 3
                                          }}
                                        >
                                          <PlayArrowIcon sx={{ fontSize: 32, color: 'white', ml: 0.5 }} />
                                        </Box>
                                      </Box>
                                    ) : (
                                      <CardMedia
                                        component="img"
                                        image={image.image_url}
                                        alt={`Instagram Image #${image.id}`}
                                        sx={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                          filter: 'brightness(1) saturate(1.1)',
                                          '&:hover': {
                                            transform: 'scale(1.05)',
                                            filter: 'brightness(1.1) saturate(1.2)'
                                          }
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    )}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        minHeight: { xs: 240, sm: 280, md: 300 },
                                        display: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                        flexDirection: 'column'
                                      }}
                                    >
                                      <Avatar sx={{
                                        bgcolor: 'rgba(52, 152, 219, 0.1)',
                                        mb: 2,
                                        width: 48,
                                        height: 48,
                                        animation: 'float 3s ease-in-out infinite'
                                      }}>
                                        <ImageIcon sx={{ fontSize: 28, color: '#3498db' }} />
                                      </Avatar>
                                      <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                                        {t('bannerImages.imageNotAvailable')}
                                      </Typography>
                                    </Box>

                                    {/* Overlay Actions */}
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.3) 100%)',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-end',
                                        p: 2
                                      }}
                                      className="card-overlay"
                                    >
                                      <Stack direction="row" spacing={1}>
                                        <Tooltip title={t('bannerImages.viewDetails')}>
                                          <IconButton
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255,255,255,0.9)',
                                              color: '#3498db',
                                              '&:hover': {
                                                bgcolor: 'white',
                                                transform: 'scale(1.1)'
                                              }
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleImageClick(image);
                                            }}
                                          >
                                            <ViewIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('bannerImages.moreOptions')}>
                                          <IconButton
                                            size="small"
                                            sx={{
                                              bgcolor: 'rgba(255,255,255,0.9)',
                                              color: '#3498db',
                                              '&:hover': {
                                                bgcolor: 'white',
                                                transform: 'scale(1.1)'
                                              }
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMenuOpen(e, image);
                                            }}
                                          >
                                            <MoreVertIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </Box>

                                  </Box>

                                  <CardActions sx={{ p: 3, pt: 2, pb: 2, minHeight: 72, justifyContent: 'center', position: 'relative', zIndex: 2 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); toggleSelect(image.id); }}
                                        sx={{
                                          bgcolor: selectedIds.has(image.id) ? 'rgba(29,78,216,0.12)' : 'rgba(255,255,255,0.9)',
                                          color: selectedIds.has(image.id) ? '#1d4ed8' : '#334155',
                                          border: '1px solid',
                                          borderColor: selectedIds.has(image.id) ? 'rgba(29,78,216,0.35)' : '#cbd5e1',
                                          borderRadius: 2,
                                          width: { xs: 48, sm: 36 }, // Larger touch target on mobile
                                          height: { xs: 48, sm: 36 }, // Larger touch target on mobile
                                          boxShadow: selectedIds.has(image.id) ? '0 4px 12px rgba(29,78,216,0.25)' : 'none',
                                          '&:hover': {
                                            bgcolor: selectedIds.has(image.id) ? 'rgba(29,78,216,0.18)' : 'white'
                                          }
                                        }}
                                        aria-label={selectedIds.has(image.id) ? 'Selected' : 'Select'}
                                      >
                                        {selectedIds.has(image.id) ? (
                                          <CheckCircleIcon fontSize="small" />
                                        ) : (
                                          <RadioButtonUncheckedIcon fontSize="small" />
                                        )}
                                      </IconButton>

                                      <Button
                                        size="medium"
                                        startIcon={<ViewIcon />}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleImageClick(image);
                                        }}
                                        sx={{
                                          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                          color: 'white',
                                          textTransform: 'none',
                                          fontWeight: 600,
                                          px: 3,
                                          py: 1,
                                          borderRadius: 2,
                                          boxShadow: selectedIds.has(image.id) ? '0 0 0 2px #1d4ed8 inset' : '0 4px 15px rgba(52, 152, 219, 0.3)',
                                          '&:hover': {
                                            background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 20px rgba(52, 152, 219, 0.4)'
                                          },
                                          transition: 'all 0.3s ease'
                                        }}
                                      >
                                        {t('bannerImages.viewDetails')}
                                      </Button>
                                    </Stack>
                                  </CardActions>
                                </Card>
                              </Zoom>
                            </Grid>
                          ))}
                        </Grid>

                        {/* Enhanced Pagination */}
                        {totalPages > 1 && (
                          <Paper
                            elevation={2}
                            sx={{
                              p: 3,
                              mt: 4,
                              borderRadius: 3,
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              border: '1px solid #dee2e6'
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
                              <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                                Page {currentPage} of {totalPages} • Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} images
                              </Typography>
                              <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                size="large"
                                sx={{
                                  '& .MuiPaginationItem-root': {
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    '&.Mui-selected': {
                                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                      color: 'white',
                                      '&:hover': {
                                        background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)'
                                      }
                                    }
                                  }
                                }}
                              />
                            </Stack>
                          </Paper>
                        )}
                      </>
                    )}
                  </Box>
                </Fade>
              )
            }

                {/* Enhanced Professional Image Detail Dialog */}
                <Dialog
                  open={dialogOpen}
                  onClose={handleCloseDialog}
                  maxWidth="lg"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {selectedImage && (
                    <>
                      <DialogTitle
                        sx={{
                          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          py: 4,
                          px: 4,
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                      >
                    {/* Background Pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                      <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        width: 56,
                        height: 56,
                        border: '2px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }}>
                        <ImageIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.5rem' }}>
                          {t('bannerImages.imageDetails')}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1rem' }}>
                          {t('bannerImages.comprehensiveImageInfo')}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={handleCloseDialog}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.25)',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
                    <Grid container>
                      <Grid item xs={12} md={7}>
                        <Box sx={{
                          p: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 500
                        }}>
                          {isVideo(selectedImage.image_url) ? (
                            <Box
                              component="video"
                              src={selectedImage.image_url}
                              controls
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 450,
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
                                }
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <Box
                              component="img"
                              src={selectedImage.image_url}
                              alt={`Instagram Image #${selectedImage.id}`}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: 450,
                                objectFit: 'contain',
                                borderRadius: 3,
                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.02)',
                                  boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
                                }
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          )}
                          <Box
                            sx={{
                              width: '100%',
                              height: 400,
                              display: 'none',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 3,
                              flexDirection: 'column',
                              border: '2px dashed #dee2e6'
                            }}
                          >
                            <Avatar sx={{
                              bgcolor: 'rgba(52, 152, 219, 0.1)',
                              mb: 3,
                              width: 80,
                              height: 80
                            }}>
                              <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />
                            </Avatar>
                            <Typography variant="h6" sx={{ color: '#495057', mb: 1, fontWeight: 600 }}>
                              {t('bannerImages.imageNotAvailable')}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Box sx={{
                          p: 4,
                          minHeight: 500,
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h5" sx={{
                            mb: 3,
                            color: '#2c3e50',
                            fontWeight: 700,
                            borderBottom: '2px solid #3498db',
                            pb: 1
                          }}>
                            {t('bannerImages.imageDetails')}
                          </Typography>

                          <Stack spacing={3}>


                            {/* Created Date */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('bannerImages.createdDate')}
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                {new Date(selectedImage.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                {new Date(selectedImage.created_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>

                            {selectedImage.updated_at !== selectedImage.created_at && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                  {t('bannerImages.lastUpdated')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {new Date(selectedImage.updated_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                  {new Date(selectedImage.updated_at).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>
                            )}

                            {/* Image URL */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('bannerImages.imageUrl')}
                              </Typography>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: '#f8f9fa',
                                  borderRadius: 2,
                                  border: '1px solid #dee2e6',
                                  wordBreak: 'break-all',
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  color: '#495057',
                                  lineHeight: 1.4
                                }}
                              >
                                {selectedImage.image_url}
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </>
                  )}
                </Dialog >

                {/* Context Menu */}
                < Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem
                    onClick={() => {
                      if (selectedImage) {
                        handleDeleteImage(selectedImage.id);
                      }
                      handleMenuClose();
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon sx={{ mr: 1 }} />
                    {t('bannerImages.deleteImage')}
                  </MenuItem>
                </Menu >

                {/* Enhanced Professional Upload Dialog */}
                < Dialog
                  open={uploadDialogOpen}
                  onClose={handleCloseUploadDialog}
                  maxWidth="md"
                  fullWidth
                  PaperProps={{
                    sx: {
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }
                  }}
                >
              <DialogTitle
                sx={{
                  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 4,
                  px: 4,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    width: 56,
                    height: 56,
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }}>
                    <UploadIcon sx={{ fontSize: 28, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.5rem' }}>
                      {t('bannerImages.uploadDialogTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1rem' }}>
                      {t('bannerImages.uploadDialogSubtitle')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseUploadDialog}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 4 }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '1px solid #dee2e6'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      {t('bannerImages.uploadRequirements')}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('bannerImages.supportedFormats')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('bannerImages.maxFileSize')}
                      </Typography>
                    </Stack>
                  </Paper>

                  {/* Quota Display */}
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: totalCount >= 3
                        ? 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(220, 53, 69, 0.1) 100%)'
                        : totalCount >= 3 * 0.8
                          ? 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(52, 152, 219, 0.1) 100%)',
                      border: `2px solid ${totalCount >= 3
                        ? '#f44336'
                        : totalCount >= 3 * 0.8
                          ? '#ffc107'
                          : '#4caf50'}`
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 0.5, fontWeight: 600 }}>
                          {t('bannerImages.bannerLimit')}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            color: totalCount >= 3 ? '#f44336' : '#2c3e50',
                            fontWeight: 700
                          }}
                        >
                          {totalCount} / 3
                        </Typography>
                      </Box>
                      {totalCount >= 3 * 0.8 && (
                        <Chip
                          label={totalCount >= 3 ? 'Full' : 'Low'}
                          size="medium"
                          sx={{
                            backgroundColor: totalCount >= 3
                              ? '#f44336'
                              : '#ffc107',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            height: 32
                          }}
                        />
                      )}
                    </Stack>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((totalCount / 3) * 100, 100)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: totalCount >= 3
                              ? '#f44336'
                              : totalCount >= 3 * 0.8
                                ? '#ffc107'
                                : '#4caf50',
                            borderRadius: 4
                          }
                        }}
                      />
                    </Box>
                    {totalCount >= 3 && (
                      <Typography variant="caption" sx={{ color: '#f44336', mt: 1, display: 'block', fontWeight: 600 }}>
                        {t('bannerImages.limitReachedInstruction')}
                      </Typography>
                    )}
                  </Paper>



                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    disabled={totalCount >= 3}
                  />
                  {totalCount < 3 && (
                    <Button
                      component="label"
                      htmlFor="image-upload"
                      variant="outlined"
                      fullWidth
                      disabled={totalCount >= 3}
                      sx={{
                        p: 4,
                        border: '2px dashed #3498db',
                        borderRadius: 3,
                        background: selectedFiles.length > 0
                          ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        transition: 'all 0.3s ease',
                        cursor: totalCount >= 3 ? 'not-allowed' : 'pointer',
                        opacity: totalCount >= 3 ? 0.6 : 1,
                        '&:hover': {
                          borderColor: totalCount >= 3 ? '#3498db' : '#2980b9',
                          backgroundColor: selectedFiles.length > 0
                            ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)',
                          transform: totalCount >= 3 ? 'none' : 'translateY(-2px)',
                          boxShadow: totalCount >= 3 ? 'none' : '0 8px 25px rgba(52, 152, 219, 0.15)'
                        }
                      }}
                    >
                      <Stack alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: selectedFiles.length > 0
                              ? 'rgba(52, 152, 219, 0.1)'
                              : 'rgba(52, 152, 219, 0.05)'
                          }}
                        >
                          <UploadIcon
                            sx={{
                              fontSize: 32,
                              color: selectedFiles.length > 0 ? '#3498db' : '#6c757d'
                            }}
                          />
                        </Avatar>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: selectedFiles.length > 0 ? '#3498db' : '#495057',
                              fontWeight: 600,
                              mb: 1
                            }}
                          >
                            {selectedFiles.length > 0
                              ? `${selectedFiles.length} ${selectedFiles.length > 1 ? t('bannerImages.filesSelectedPlural') : t('bannerImages.filesSelected')}`
                              : t('bannerImages.clickToSelect')
                            }
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6c757d' }}>
                            {selectedFiles.length > 0
                              ? t('bannerImages.readyToUpload')
                              : t('bannerImages.dragDropHint')
                            }
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#adb5bd', mt: 1, display: 'block' }}>
                            {t('bannerImages.maxFileSizeHint')}
                          </Typography>
                        </Box>
                      </Stack>
                    </Button>
                  )}

                  {selectedFiles.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('bannerImages.selectedFiles')} ({selectedFiles.length})
                      </Typography>
                      <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                        <Stack spacing={2}>
                          {selectedFiles.map((file, index) => (
                            <Paper
                              key={index}
                              elevation={0}
                              sx={{
                                p: 2,
                                bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                borderRadius: 2,
                                border: '1px solid #dee2e6'
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: 'rgba(52, 152, 219, 0.1)', width: 40, height: 40 }}>
                                  <ImageIcon sx={{ color: '#3498db', fontSize: 20 }} />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                    {file.name}
                                  </Typography>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Chip
                                      label={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                    <Chip
                                      label={file.type.split('/')[1]?.toUpperCase() || 'IMAGE'}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                  </Stack>
                                </Box>
                                <CheckCircleIcon sx={{ color: '#3498db', fontSize: 24 }} />
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    </Paper>
                  )}

                  {uploadingFiles.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('bannerImages.uploadProgress')}
                      </Typography>
                      <Stack spacing={2}>
                        {uploadingFiles.map((file, index) => (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: file.status === 'completed'
                                ? 'linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%)'
                                : file.status === 'error'
                                  ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)'
                                  : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 2,
                              border: `1px solid ${file.status === 'completed' ? '#28a745' : file.status === 'error' ? '#dc3545' : '#dee2e6'}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: file.status === 'completed'
                                  ? 'rgba(52, 152, 219, 0.1)'
                                  : file.status === 'error'
                                    ? 'rgba(220, 53, 69, 0.1)'
                                    : 'rgba(52, 152, 219, 0.1)',
                                width: 40,
                                height: 40
                              }}>
                                {file.status === 'completed' ? (
                                  <CheckCircleIcon sx={{ color: '#3498db', fontSize: 20 }} />
                                ) : file.status === 'error' ? (
                                  <ErrorIcon sx={{ color: '#dc3545', fontSize: 20 }} />
                                ) : (
                                  <CircularProgress size={20} sx={{ color: '#3498db' }} />
                                )}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {file.name}
                                </Typography>
                                {file.status === 'uploading' && (
                                  <LinearProgress
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: '#3498db'
                                      }
                                    }}
                                  />
                                )}
                                {file.error && (
                                  <Typography variant="caption" sx={{ color: '#dc3545', mt: 1, display: 'block' }}>
                                    {file.error}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" sx={{
                                color: file.status === 'completed' ? '#3498db' :
                                  file.status === 'error' ? '#dc3545' : '#6c757d',
                                fontWeight: 600
                              }}>
                                {file.status === 'completed' ? t('bannerImages.completed') :
                                  file.status === 'error' ? t('bannerImages.failed') : t('bannerImages.uploadingProgress')}
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>
                  )}

                  {uploading && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #3498db',
                        bgcolor: 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#3498db' }} />
                        <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                          {t('bannerImages.uploadingToR2')}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(52, 152, 219, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#3498db'
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 1, textAlign: 'center' }}>
                        {uploadProgress.toFixed(0)}{t('bannerImages.percentComplete')}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }} />
                  {totalCount < 3 && (
                    <Button
                      onClick={handleUpload}
                      variant="contained"
                      disabled={selectedFiles.length === 0 || uploading}
                      startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                          transform: 'translateY(-1px)'
                        },
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        fontWeight: 600,
                        color: 'white !important',
                        textTransform: 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {uploading
                        ? `${t('bannerImages.uploadingProgress')} (${uploadProgress.toFixed(0)}%)`
                        : `${t('bannerImages.uploadAndAdd')} ${selectedFiles.length} ${selectedFiles.length > 1 ? t('bannerImages.uploadAndAddFilesPlural') : t('bannerImages.uploadAndAddFiles')}`
                      }
                    </Button>
                  )}
                </Stack>
              </DialogActions>
                </Dialog >
          </Box >
        </Paper >
      </Container >
    </>
  );
};

export default BannerImages;
